import { FC } from 'react';
import Loader from '@components/Loader/ui/Loader.tsx';
import styles from './StatusTag.module.scss';

export enum Status {
  Completed = 'Completed',
  Pending = 'Pending',
  Draft = 'Draft',
  Failed = 'Failed',
  DocumentsParsed = 'Document(s) parsed',
  ParsingDocuments = 'Parsing document(s)',
  InProgress = 'In progress',
  ParsingFailed = 'Document(s) parsing failed',
  Published = 'Published',
}

interface StatusTagProps {
  status: string;
}

const toCanonicalStatus = (raw: string): string => {
  const value = String(raw ?? '').trim();
  if (!value) return '';

  const lower = value.toLowerCase();
  switch (lower) {
    case 'completed':
      return Status.Completed;
    case 'pending':
      return Status.Pending;
    case 'draft':
      return Status.Draft;
    case 'failed':
      return Status.Failed;
    case 'published':
      return Status.Published;
    case 'in progress':
    case 'in_progress':
    case 'inprogress':
      return Status.InProgress;
    case 'document(s) parsed':
    case 'documents parsed':
    case 'document parsed':
      return Status.DocumentsParsed;
    case 'parsing document(s)':
    case 'parsing documents':
    case 'parsing document':
      return Status.ParsingDocuments;
    case 'document(s) parsing failed':
    case 'documents parsing failed':
    case 'document parsing failed':
      return Status.ParsingFailed;
    default:
      return value;
  }
};

/**
 * @param props - The props for the component.
 * @param status - Status of the tag.
 * @returns A React component that displays an status tag.
 */

const StatusTag: FC<StatusTagProps> = (props) => {
  const rawStatus = String(props.status ?? '').trim();
  const normalizedRaw = rawStatus.replace(/^•\s*/, '').trim();
  const status = toCanonicalStatus(normalizedRaw);
  const label =
    status === Status.Pending ? 'In progress' : status || normalizedRaw;
  let classname = '';
  switch (status) {
    case Status.Completed:
      classname = styles.completed;
      break;
    case Status.Draft:
      classname = styles.draft;
      break;
    case Status.Pending:
      classname = styles.pending;
      break;
    case Status.Failed:
      classname = styles.failed;
      break;
    case Status.DocumentsParsed:
      classname = styles.parsed;
      break;
    case Status.ParsingDocuments:
      classname = styles.parsing;
      break;
    case Status.ParsingFailed:
      classname = styles.parsingFailed;
      break;
    case Status.Published:
      classname = styles.completed;
      break;
  }
  return (
    <div className={[styles.statusTag, classname].join(' ')}>
      {(status === Status.Pending || status === Status.ParsingDocuments) && (
        <Loader size={12} />
      )}
      {status !== Status.Pending && status !== Status.ParsingDocuments && (
        <span />
      )}
      <p>{label}</p>
    </div>
  );
};

export default StatusTag;
